import { api } from "../lib/api";

export interface TransactionSplit {
  id: string;
  amount: string;
  category_id: string;
  category_name: string;
  loan_id: string | null;
  notes: string | null;
}

export interface TransactionSplitInput {
  amount: string;
  category_id: string;
  loan_id?: string | null;
  notes?: string | null;
}

export interface BankTransaction {
  id: string;
  bank_account_id: string;
  bank_account_name: string;
  date: string;
  description: string;
  amount: string;
  balance_after: string | null;
  category_id: string | null;
  category_name: string | null;
  categorised_by: "rule" | "manual" | null;
  rule_id: string | null;
  allocations: TransactionAllocation[];
  allocated_total: string;
  loan_id: string | null;
  notes: string | null;
  is_transfer: boolean;
  is_personal: boolean;
  has_splits: boolean;
  splits: TransactionSplit[];
  created_at: string;
  updated_at: string;
  closed_year_warning: string | null;
}

export interface TransactionAllocation {
  payment_id: string;
  invoice_id: string;
  invoice_number: string | null;
  client_name: string;
  amount: string;
  /** The invoice's own figures. `invoice_amount_paid` includes this allocation, so the
   * outstanding it would have without this receipt is total - paid + amount. */
  invoice_total: string;
  invoice_amount_paid: string;
}

export interface TransactionFilters {
  account?: string;
  date_from?: string;
  date_to?: string;
  uncategorised?: boolean;
  category_id?: string;
  loan_id?: string;
  q?: string;
  page?: number;
  page_size?: number;
}

export interface TransactionUpdateInput {
  category_id?: string | null;
  is_transfer?: boolean;
  is_personal?: boolean;
  notes?: string | null;
  loan_id?: string | null;
  updated_at: string;
}

function buildQuery(filters: TransactionFilters): string {
  const params = new URLSearchParams();
  if (filters.account) params.set("account", filters.account);
  if (filters.date_from) params.set("date_from", filters.date_from);
  if (filters.date_to) params.set("date_to", filters.date_to);
  if (filters.uncategorised) params.set("uncategorised", "true");
  if (filters.category_id) params.set("category_id", filters.category_id);
  if (filters.loan_id) params.set("loan_id", filters.loan_id);
  if (filters.q) params.set("q", filters.q);
  params.set("page", String(filters.page ?? 1));
  params.set("page_size", String(filters.page_size ?? 50));
  return params.toString();
}

export async function listTransactions(
  filters: TransactionFilters = {},
): Promise<{ items: BankTransaction[]; total: number }> {
  return api.getPaged<BankTransaction>(`/api/transactions?${buildQuery(filters)}`);
}

export function updateTransaction(
  id: string,
  data: TransactionUpdateInput,
): Promise<BankTransaction> {
  return api.put<BankTransaction>(`/api/transactions/${id}`, data);
}

export function replaceTransactionSplits(
  id: string,
  splits: TransactionSplitInput[],
): Promise<BankTransaction> {
  return api.put<BankTransaction>(`/api/transactions/${id}/splits`, { splits });
}

export interface BulkActionInput {
  transaction_ids: string[];
  category_id?: string | null;
  is_transfer?: boolean;
  is_personal?: boolean;
}

export interface BulkActionResult {
  updated_count: number;
  closed_year_warning: string | null;
}

export function bulkUpdateTransactions(data: BulkActionInput): Promise<BulkActionResult> {
  return api.post<BulkActionResult>("/api/transactions/bulk", data);
}
